import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { Camera, X, Sparkles, RefreshCw, SwitchCamera, ShoppingBag, Eye, AlertTriangle, PackageOpen, Loader2 } from "lucide-react";
import { Product } from "../../redux/productSlice";

/* ═══════════════════════════════════════════════════════════════════
   BeautyAI: camera skin-tone check + product matcher
   • camera only (no gallery / file input anywhere)
   • desktop: floating modal · mobile: draggable bottom sheet
   • BeautyDetector API first; if it is down or not live yet, an on-device
     colour estimate takes over so recommendations still work
   • matching reads each product's title, description, category, colours
   ═══════════════════════════════════════════════════════════════════ */

/** Change this to your real BeautyDetector endpoint (or pass the apiUrl prop).
 *  Expected: POST multipart/form-data, field "image" (JPEG). See fromApi() for the tolerant response parser. */
export const BEAUTY_API_URL = "/api/beauty-detector";

type Props = {
  open: boolean;
  onClose: () => void;
  products: Product[];
  onView?: (productId: string) => void;
  onAdd?: (p: Product) => void;
  apiUrl?: string;
  apiKey?: string;
};

/* ═══════════════ colour science ═══════════════ */
type Undertone = "warm" | "cool" | "neutral";
type Tone = { hex: string; depth: number; undertone: Undertone; label: string; skinType?: string; concerns: string[] };

const DEPTHS = ["Fair", "Light", "Medium", "Tan", "Deep", "Rich"];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgbToHex = (r: number, g: number, b: number) =>
  "#" + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");

const rgbToLab = (r: number, g: number, b: number): [number, number, number] => {
  const lin = (c: number) => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const [R, G, B] = [lin(r), lin(g), lin(b)];
  const X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  return [116 * f(Y) - 16, 500 * (f(X) - f(Y)), 200 * (f(Y) - f(Z))];
};
const labOf = (hex: string) => rgbToLab(...hexToRgb(hex));
const deltaE = (a: string, b: string) => {
  const x = labOf(a), y = labOf(b);
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
};

function describeTone(hex: string): Pick<Tone, "depth" | "undertone" | "label" | "hex"> {
  const [L, a, b] = labOf(hex);
  const depth = L >= 76 ? 1 : L >= 66 ? 2 : L >= 56 ? 3 : L >= 45 ? 4 : L >= 33 ? 5 : 6;
  const hue = (Math.atan2(b, a) * 180) / Math.PI;
  const undertone: Undertone = hue >= 58 ? "warm" : hue <= 47 ? "cool" : "neutral";
  return { hex, depth, undertone, label: `${DEPTHS[depth - 1]} · ${cap(undertone)}` };
}

/** average the cheek/forehead zone, ignoring the darkest and brightest pixels (shadows, shine) */
function sampleSkin(ctx: CanvasRenderingContext2D, w: number, h: number): { hex: string; light: number } | null {
  try {
    const x0 = Math.round(w * 0.33), y0 = Math.round(h * 0.3), sw = Math.round(w * 0.34), sh = Math.round(h * 0.32);
    const d = ctx.getImageData(x0, y0, sw, sh).data;
    const px: [number, number, number, number][] = [];
    for (let i = 0; i < d.length; i += 8) px.push([0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2], d[i], d[i + 1], d[i + 2]]);
    px.sort((p, q) => p[0] - q[0]);
    const mid = px.slice(Math.floor(px.length * 0.2), Math.ceil(px.length * 0.8));
    if (!mid.length) return null;
    const avg = [1, 2, 3].map((k) => mid.reduce((s, p) => s + p[k], 0) / mid.length);
    return { hex: rgbToHex(avg[0], avg[1], avg[2]), light: mid.reduce((s, p) => s + p[0], 0) / mid.length };
  } catch { return null; }
}

/* ═══════════════ BeautyDetector API ═══════════════ */
const pick = (o: any, ...keys: string[]) => { for (const k of keys) if (o && o[k] != null) return o[k]; return undefined; };
const isHex = (v: any) => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v.trim());

/** tolerant: accepts {skinTone:{hex,name,undertone}}, {skin_tone:"#c68642"}, {hex}, wrapped in result/data, etc. */
function fromApi(j: any): Partial<Tone> | null {
  const root = j?.result ?? j?.data ?? j;
  const t = pick(root, "skinTone", "skin_tone", "tone");
  const hexRaw = isHex(t) ? t : pick(t, "hex", "color") ?? pick(root, "hex", "skinHex", "skin_hex");
  const nameRaw = typeof t === "string" && !isHex(t) ? t : pick(t, "name", "label", "shade", "depth");
  const under = String(pick(t, "undertone") ?? pick(root, "undertone") ?? "").toLowerCase();
  const typeRaw = pick(root, "skinType", "skin_type", "type");
  const concernsRaw = pick(root, "concerns", "issues", "conditions");
  const concerns: string[] = Array.isArray(concernsRaw)
    ? concernsRaw.map((c: any) => String(typeof c === "string" ? c : c?.name ?? c?.label ?? "")).filter(Boolean) : [];

  let base: Partial<Tone> = {};
  if (isHex(hexRaw)) base = describeTone(String(hexRaw).trim());
  else if (nameRaw) {
    const di = DEPTHS.findIndex((d) => String(nameRaw).toLowerCase().includes(d.toLowerCase()));
    if (di < 0) return null;
    base = { depth: di + 1 };
  } else return null;

  const ut: Undertone | undefined = under.includes("warm") || under.includes("yellow") || under.includes("golden") ? "warm"
    : under.includes("cool") || under.includes("pink") || under.includes("rosy") ? "cool"
    : under.includes("neutral") ? "neutral" : undefined;
  const undertone = ut ?? base.undertone ?? "neutral";
  const depth = base.depth ?? 3;
  return { ...base, depth, undertone, label: `${DEPTHS[depth - 1]} · ${cap(undertone)}`, skinType: typeRaw ? String(typeRaw).toLowerCase() : undefined, concerns };
}

async function callApi(url: string, key: string | undefined, blob: Blob): Promise<any> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 10000);
  try {
    const fd = new FormData();
    fd.append("image", blob, "capture.jpg");
    const r = await fetch(url, { method: "POST", body: fd, signal: ctl.signal, headers: key ? { Authorization: `Bearer ${key}` } : undefined });
    if (!r.ok) throw new Error(`BeautyDetector ${r.status}`);
    return await r.json();
  } finally { clearTimeout(timer); }
}

/* ═══════════════ understanding what the person typed ═══════════════ */
const TRIGGERS: Record<string, RegExp> = {
  glow: /glow|radian|luminous|dewy/,
  matte: /matte|oily|\boil\b|greas|shiny/,
  hydrating: /hydrat|moistur|\bdry\b|flaky|dehydrat/,
  brightening: /bright|dull|dark spot|pigment|even tone|uneven|hyperpig/,
  "anti-aging": /aging|ageing|wrinkle|fine line|mature/,
  acne: /acne|pimple|breakout|blemish|\bpores?\b|blackhead/,
  soothing: /sensitiv|redness|irritat|rosacea|calm|sooth|eczema/,
  coverage: /coverage|cover\b|conceal/,
  natural: /natural|vegan|organic|\bclean\b|cruelty/,
  spf: /\bspf\b|\bsun/,
  "long-lasting": /long.?last|waterproof|smudge|all day/,
};
const EFFECT_WORDS: Record<string, string[]> = {
  glow: ["glow", "radian", "luminous", "dewy", "illuminat", "highlight"],
  matte: ["matte", "mattifying", "oil control", "oil-free", "shine control"],
  hydrating: ["hydrat", "moistur", "hyaluronic", "ceramide", "nourish", "plump"],
  brightening: ["brighten", "vitamin c", "niacinamide", "even tone", "dark spot", "dullness"],
  "anti-aging": ["anti-aging", "anti aging", "wrinkle", "fine line", "retinol", "peptide", "firming", "collagen"],
  acne: ["acne", "blemish", "salicylic", "breakout", "pore", "clarif", "zinc"],
  soothing: ["sooth", "calm", "redness", "aloe", "centella", "cica", "gentle", "hypoallergenic", "sensitive"],
  coverage: ["coverage", "buildable", "concealer", "full cover"],
  natural: ["natural", "vegan", "organic", "clean", "plant-based", "cruelty"],
  spf: ["spf", "sun protection", "sunscreen", "uv"],
  "long-lasting": ["long-lasting", "long lasting", "waterproof", "transfer-proof", "24h", "24-hour"],
};
const ALLERGY_SYN: Record<string, string[]> = {
  fragrance: ["fragrance", "parfum", "perfume", "scent"],
  paraben: ["paraben"],
  sulfate: ["sulfate", "sulphate", "sls", "sles"],
  alcohol: ["alcohol", "ethanol", "alcohol denat"],
  nut: ["nut", "almond", "peanut", "walnut", "hazelnut", "cashew", "pistachio", "macadamia"],
  coconut: ["coconut"], shea: ["shea"], soy: ["soy"], gluten: ["gluten", "wheat"], latex: ["latex"],
  lanolin: ["lanolin"], nickel: ["nickel"], retinol: ["retinol", "retinoid"], silicone: ["silicone", "dimethicone", "siloxane"],
  talc: ["talc"], dairy: ["milk", "lactose", "dairy"], honey: ["honey", "beeswax", "propolis"], carmine: ["carmine", "cochineal"],
  "essential oil": ["essential oil", "lavender", "peppermint", "eucalyptus", "citrus", "tea tree"],
};
const STOP = new Set(["want", "need", "skin", "with", "that", "have", "looking", "something", "makeup", "would", "like", "really", "very", "good", "also", "please", "product", "products", "feel", "feels", "some", "more", "less", "from", "this", "love", "help", "give", "show", "make", "gets", "keep"]);

type Needs = { effects: string[]; avoid: string[]; extras: string[] };
function parseNeeds(text: string): Needs {
  let rest = text.toLowerCase();
  const avoid: string[] = [];
  const re = /(?:allerg(?:ic|y|ies)?(?:\s+(?:to|with))?|sensitive\s+to|intolerant\s+to|react(?:s|ion)?\s+to|break(?:s|ing)?\s+out\s+(?:from|with)|avoid(?:ing)?|without)\s+([^.;\n]+)/g;
  rest = rest.replace(re, (_m, list: string) => {
    list.split(/\b(?:want|need|looking|prefer|please|but)\b/)[0]
      .split(/,|\band\b|&|\/|\bor\b/)
      .map((s) => s.replace(/\b(any|all|the|products?|ingredients?|with|in|a|an)\b/g, "").trim())
      .filter((s) => s.length > 2).forEach((s) => avoid.push(s));
    return " ";
  });
  const effects = Object.keys(TRIGGERS).filter((k) => TRIGGERS[k].test(rest));
  const extras = Array.from(new Set((rest.match(/[a-z]{4,}/g) || []).filter((w) => !STOP.has(w) && !effects.some((e) => TRIGGERS[e].test(w))))).slice(0, 6);
  return { effects, avoid: Array.from(new Set(avoid)), extras };
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const expandAllergen = (term: string): string[] => {
  const t = term.toLowerCase().replace(/(?:es|s)$/, "");
  const hit = Object.entries(ALLERGY_SYN).find(([k, v]) => k === t || k === term || v.includes(t) || v.includes(term));
  return hit ? hit[1] : [term];
};
/** true when the text contains the ingredient and does NOT say "fragrance-free" / "free of ... fragrance" / "without ..." */
function containsAllergen(text: string, words: string[]): boolean {
  for (const w of words) {
    const re = new RegExp(`\\b${esc(w)}(?:s|es)?\\b`, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const before = text.slice(Math.max(0, m.index - 45), m.index);
      const after = text.slice(m.index + m[0].length, m.index + m[0].length + 8);
      const negated = /\b(free (?:of|from)|without|no|non|sans|zero)\b[^.;]*$/.test(before) || /^[\s-]{0,2}free\b/.test(after);
      if (!negated) return true;
    }
  }
  return false;
}

/* ═══════════════ product helpers ═══════════════ */
type Variant = { color: string; hex?: string | null; images?: string[] };
const variantsOf = (p: Product): Variant[] => (((p as any).variants as Variant[]) || []).filter((v) => v && v.color);
const coverOf = (p: Product): string | undefined => (p as any).images?.[0] || variantsOf(p).flatMap((v) => v.images || [])[0];
const money = (v: any) => `€${(Number(v) || 0).toFixed(2)}`;

const textOf = (p: Product) =>
  [p.name, (p as any).description, (p as any).ingredients, (p as any).categoryName, (p as any).subcategoryName,
   ...Object.values(((p as any).attributes || {}) as Record<string, any>).flat(), ...variantsOf(p).map((v) => v.color)]
    .filter(Boolean).join(" ").toLowerCase();

const BEAUTY_RE = /foundation|concealer|powder|blush|bronzer|highlighter|contour|lipstick|\blip\b|gloss|balm|tint|mascara|eyeliner|eyeshadow|eye shadow|palette|primer|setting spray|skincare|serum|moistur|cream|cleanser|toner|sunscreen|\bspf\b|mask|makeup|make-up|cosmetic|beauty|nail polish|lotion|skin/;
const kindOf = (p: Product): "base" | "color" | "care" | "other" => {
  const t = `${p.name} ${(p as any).categoryName ?? ""} ${(p as any).subcategoryName ?? ""}`.toLowerCase();
  if (/foundation|concealer|bb cream|cc cream|skin tint|tinted|compact|powder/.test(t)) return "base";
  if (/\blip|blush|bronzer|eyeshadow|eye shadow|eyeliner|mascara|nail|polish|palette|highlighter|contour/.test(t)) return "color";
  if (/serum|cleanser|moistur|cream|sunscreen|\bspf\b|toner|mask|lotion|essence|\boil\b/.test(t)) return "care";
  return "other";
};

const SHADE_WORDS: string[][] = [
  ["ivory", "porcelain", "fair", "alabaster", "vanilla"],
  ["light", "beige", "nude", "buff", "sand", "cream"],
  ["medium", "honey", "golden", "natural", "caramel"],
  ["tan", "bronze", "amber", "almond", "toffee"],
  ["deep", "mocha", "chestnut", "cocoa", "brown"],
  ["espresso", "ebony", "rich", "dark", "chocolate", "mahogany"],
];
const UNDERTONE_WORDS: Record<Undertone, string[]> = {
  warm: ["warm", "golden", "yellow", "peach", "honey"],
  cool: ["cool", "rosy", "pink", "rose"],
  neutral: ["neutral", "natural"],
};
const FLATTER: Record<Undertone, string[]> = {
  warm: ["coral", "peach", "orange", "terracotta", "brick", "rust", "gold", "bronze", "copper", "apricot", "tangerine", "caramel", "brown", "olive"],
  cool: ["pink", "rose", "berry", "plum", "mauve", "fuchsia", "magenta", "lilac", "burgundy", "wine", "cherry", "silver", "purple"],
  neutral: ["rose", "nude", "mauve", "berry", "coral", "red", "taupe", "peach", "pink", "brown"],
};
const DEPTH_FLATTER = (d: number) => d <= 2 ? ["soft", "pastel", "sheer", "light", "baby"] : d >= 5 ? ["bold", "deep", "vivid", "bright", "burgundy", "plum", "wine", "chocolate", "fuchsia"] : [];

type Rec = { p: Product; score: number; reasons: string[]; bestShade?: string; bestHex?: string };

function recommend(products: Product[], tone: Tone | null, needs: Needs) {
  const beauty = products.filter((p) => BEAUTY_RE.test(textOf(p)));
  const bans = needs.avoid.map((a) => ({ term: a, words: expandAllergen(a) }));
  const hidden = new Set<string>();
  const recs: Rec[] = [];

  /* skin type + concerns from the scan act as softer wishes */
  const implied: string[] = tone ? Object.keys(TRIGGERS).filter((k) => TRIGGERS[k].test(`${tone.skinType ?? ""} ${tone.concerns.join(" ")}`.toLowerCase()) && !needs.effects.includes(k)) : [];

  for (const p of beauty) {
    const t = textOf(p);
    const banned = bans.find((b) => containsAllergen(t, b.words));
    if (banned) { hidden.add(banned.term); continue; }

    let score = 5;
    const reasons: string[] = [];
    const kind = kindOf(p);
    const vs = variantsOf(p);
    let bestShade: string | undefined, bestHex: string | undefined;

    if (tone) {
      if (kind === "base") {
        let bestD = Infinity;
        for (const v of vs) {
          let s = 0, d = Infinity;
          if (v.hex && isHex(v.hex)) d = deltaE(tone.hex, v.hex);
          if (d < 6) s = 40; else if (d < 12) s = 28; else if (d < 20) s = 14;
          const name = v.color.toLowerCase();
          if (!v.hex) {
            if (SHADE_WORDS[tone.depth - 1].some((w) => name.includes(w))) s = Math.max(s, 30);
            else if ([-1, 1].some((o) => SHADE_WORDS[tone.depth - 1 + o]?.some((w) => name.includes(w)))) s = Math.max(s, 12);
            d = 30 - s;
          }
          if (UNDERTONE_WORDS[tone.undertone].some((w) => name.includes(w))) s += 10;
          if (d < bestD && s > 0) { bestD = d; bestShade = v.color; bestHex = v.hex ?? undefined; score += s; }
        }
        if (!vs.length) {
          if (SHADE_WORDS[tone.depth - 1].some((w) => t.includes(w))) { score += 20; reasons.push(`Made for ${DEPTHS[tone.depth - 1].toLowerCase()} skin`); }
        } else if (bestShade) reasons.push(`Closest shade: ${bestShade}`);
      } else if (kind === "color") {
        const pool = [...FLATTER[tone.undertone], ...DEPTH_FLATTER(tone.depth)];
        const names = `${vs.map((v) => v.color).join(" ")} ${t}`;
        const hits = pool.filter((w) => names.includes(w));
        if (hits.length) {
          score += Math.min(30, hits.length * 10);
          reasons.push(`Flatters ${tone.undertone} tones: ${hits.slice(0, 2).join(", ")}`);
          const v = vs.find((x) => hits.some((w) => x.color.toLowerCase().includes(w)));
          if (v) { bestShade = v.color; bestHex = v.hex ?? undefined; }
        }
      }
    }

    for (const e of needs.effects) if (EFFECT_WORDS[e].some((w) => t.includes(w))) { score += 16; reasons.push(cap(e)); }
    for (const e of implied) if (EFFECT_WORDS[e].some((w) => t.includes(w))) { score += 10; reasons.push(`Good for ${tone?.skinType && TRIGGERS[e].test(tone.skinType) ? tone.skinType + " skin" : e}`); }
    for (const x of needs.extras) if (t.includes(x)) { score += 6; reasons.push(cap(x)); }
    if (bans.length && !hidden.size) { /* kept: no listed ingredient found */ }

    recs.push({ p, score, reasons: Array.from(new Set(reasons)), bestShade, bestHex });
  }
  recs.sort((a, b) => b.score - a.score);
  return { recs: recs.slice(0, 8), hidden: Array.from(hidden), beautyCount: beauty.length };
}

/* ═══════════════ styles (same black / glossy-yellow language as the shop) ═══════════════ */
const INK = "#000", YEL = "#FFD60A", YEL_GLOSS = "linear-gradient(180deg,#FFF27A 0%,#FFD60A 52%,#F2B800 100%)", RASP = "#E30B5C", BODY = "#5C5C5C", LINE = "#E5E5E5";
const CSS = `
.bai, .bai *, .bai *::before, .bai *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.bai button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.bai button:disabled{ opacity:.45; cursor:not-allowed; }
.bai button:focus-visible, .bai textarea:focus-visible{ outline:3px solid ${INK}; outline-offset:2px; }
.bai-scrim{ position:fixed; inset:0; z-index:450; display:flex; background:rgba(0,0,0,.55); }
.bai-scrim.d{ align-items:center; justify-content:center; padding:24px; }
.bai-scrim.m{ align-items:flex-end; }
.bai-panel{ position:relative; display:flex; flex-direction:column; width:100%; background:#fff; color:${INK}; border:1px solid ${INK}; overflow:hidden; }
.bai-panel.d{ max-width:1000px; max-height:90vh; border-radius:30px; box-shadow:0 0 0 4px #fff, 0 0 0 5px ${INK}, 0 40px 80px -20px rgba(0,0,0,.55); }
.bai-panel.m{ max-height:94dvh; border-radius:28px 28px 0 0; border-bottom:none; padding-bottom:env(safe-area-inset-bottom); }
.bai-handle{ flex:none; padding:12px 0 6px; touch-action:none; cursor:grab; }
.bai-handle i{ display:block; width:42px; height:4px; margin:0 auto; border-radius:999px; background:#CFCFCF; }
.bai-head{ flex:none; display:flex; align-items:center; gap:12px; padding:18px 22px 12px; }
.bai-panel.m .bai-head{ padding:4px 18px 10px; }
.bai-head h2{ margin:0; font-family:'Cormorant Garamond',serif; font-size:32px; font-weight:700; line-height:1; }
.bai-head p{ margin:4px 0 0; font-size:13px; color:${BODY}; }
.bai-x{ margin-left:auto; flex:none; width:40px; height:40px; border-radius:50%; border:1px solid ${INK} !important; background:linear-gradient(180deg,#fff,#eee) !important; display:flex; align-items:center; justify-content:center; transition:background .2s; }
.bai-x:hover{ background:${YEL_GLOSS} !important; }
.bai-body{ flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; display:grid; gap:22px; padding:8px 22px 24px; grid-template-columns:minmax(0,5fr) minmax(0,6fr); align-items:start; }
.bai-panel.m .bai-body{ grid-template-columns:1fr; padding:6px 16px 22px; }

.bai-stage{ position:relative; aspect-ratio:4/5; border-radius:22px; overflow:hidden; background:#111; border:1px solid ${INK}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${INK}; display:flex; align-items:center; justify-content:center; color:#fff; }
.bai-panel.m .bai-stage{ aspect-ratio:1/1; }
.bai-stage video, .bai-stage img.still{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.bai-stage video.mirror{ transform:scaleX(-1); }
.bai-oval{ position:absolute; left:50%; top:46%; width:58%; height:72%; transform:translate(-50%,-50%); border-radius:50%; border:2px dashed rgba(255,255,255,.85); box-shadow:0 0 0 999px rgba(0,0,0,.35); pointer-events:none; }
.bai-ph{ display:flex; flex-direction:column; align-items:center; gap:12px; text-align:center; padding:24px; max-width:300px; }
.bai-ph p{ margin:0; font-size:14px; line-height:1.45; color:rgba(255,255,255,.78); }
.bai-over{ position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; background:rgba(0,0,0,.55); font-size:14px; }
.bai-spin{ animation:baiSpin 1s linear infinite; }
@keyframes baiSpin{ to{ transform:rotate(360deg); } }
.bai-ctrl{ position:absolute; left:0; right:0; bottom:14px; display:flex; align-items:center; justify-content:center; gap:18px; }
.bai-shutter{ width:68px; height:68px; border-radius:50%; background:#fff !important; border:4px solid ${INK} !important; box-shadow:0 0 0 3px #fff, 0 6px 14px rgba(0,0,0,.5); transition:transform .15s, background .2s; }
.bai-shutter:hover{ background:${YEL_GLOSS} !important; }
.bai-shutter:active{ transform:scale(.92); }
.bai-mini{ width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,.22) !important; border:1px solid rgba(255,255,255,.55) !important; color:#fff !important; display:flex; align-items:center; justify-content:center; -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px); transition:background .2s, color .2s; }
.bai-mini:hover{ background:${YEL} !important; color:${INK} !important; }

.bai-btn{ position:relative; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:48px; padding:0 24px; border-radius:999px; border:1px solid ${INK} !important; color:#fff !important;
  font-size:11.5px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; background:linear-gradient(180deg,#4a4a4a,#111 48%,#000);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.4), 0 5px 0 #2b2b2b; transition:background .2s, color .2s, transform .15s, box-shadow .15s; }
.bai-btn:hover:not(:disabled){ background:${YEL_GLOSS}; color:${INK} !important; transform:translateY(-2px); box-shadow:inset 0 2px 2px rgba(255,255,255,.8), 0 7px 0 #C99700; }
.bai-btn:active:not(:disabled){ transform:translateY(4px); box-shadow:0 1px 0 #C99700; }
.bai-btn.ghost{ background:linear-gradient(180deg,#fff,#eee); color:${INK} !important; box-shadow:inset 0 2px 2px #fff, 0 5px 0 ${INK}; }
.bai-btn.sm{ height:38px; padding:0 16px; font-size:10.5px; letter-spacing:.14em; box-shadow:inset 0 2px 2px rgba(255,255,255,.4), 0 3px 0 #2b2b2b; }
.bai-btn.sm.ghost{ box-shadow:inset 0 2px 2px #fff, 0 3px 0 ${INK}; }
.bai-btn.block{ width:100%; margin-top:10px; }

.bai-err{ display:flex; gap:10px; align-items:flex-start; margin-top:12px; padding:12px 14px; border:1px solid ${RASP}; border-radius:14px; font-size:13px; line-height:1.4; color:#8F0636; background:#FFF0F6; }
.bai-note{ margin:12px 0 0; padding:10px 14px; border-radius:14px; background:#FFF8CC; border:1px solid #E8D36A; font-size:12.5px; line-height:1.4; }
.bai-tone{ display:flex; align-items:center; gap:14px; margin-top:14px; padding:14px; border:1px solid ${INK}; border-radius:18px; }
.bai-swatch{ flex:none; width:56px; height:56px; border-radius:50%; border:2px solid ${INK}; box-shadow:inset -4px -5px 9px rgba(0,0,0,.25), inset 2px 2px 5px rgba(255,255,255,.5); }
.bai-tone b{ display:block; font-family:'Cormorant Garamond',serif; font-size:26px; line-height:1.05; }
.bai-tone small{ color:${BODY}; font-size:12px; }
.bai-chips{ display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.bai-chip{ display:inline-flex; align-items:center; height:26px; padding:0 11px; border-radius:999px; border:1px solid ${INK}; background:#fff; font-size:11.5px; }
.bai-chip.no{ border-color:${RASP}; color:#8F0636; background:#FFF0F6; }
.bai-chip.fill{ background:${INK}; color:#fff; }

.bai-label{ display:block; margin:0 0 8px; font-size:11px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; }
.bai-text{ width:100%; min-height:84px; resize:vertical; padding:12px 14px; border-radius:14px; border:1px solid #BDBDBD; font-size:15px; line-height:1.4; color:${INK}; background:#fff; outline:none; transition:border-color .2s, box-shadow .2s; }
.bai-text:focus{ border-color:${INK}; box-shadow:0 0 0 3px ${YEL}; }
.bai-hint{ margin:6px 0 0; font-size:12px; color:${BODY}; }
.bai-sec{ margin-top:20px; display:flex; align-items:baseline; justify-content:space-between; gap:10px; }
.bai-sec h3{ margin:0; font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:700; }
.bai-list{ display:flex; flex-direction:column; gap:12px; margin-top:12px; }
.bai-rec{ display:flex; gap:14px; padding:12px; border:1px solid ${INK}; border-radius:20px; background:linear-gradient(180deg,#fff,#fafafa); box-shadow:0 3px 0 ${INK}; transition:box-shadow .2s, transform .2s; }
.bai-rec:hover{ transform:translateY(-2px); box-shadow:0 0 0 4px ${YEL}, 0 0 0 5px ${INK}; }
.bai-rec.top{ background:linear-gradient(180deg,#FFFBE0,#fff); }
.bai-pic{ flex:none; width:92px; height:92px; border-radius:14px; overflow:hidden; background:#F4F1EA; border:1px solid ${LINE}; display:flex; align-items:center; justify-content:center; color:#6B6B6B; }
.bai-pic img{ width:100%; height:100%; object-fit:cover; }
.bai-info{ flex:1; min-width:0; display:flex; flex-direction:column; gap:6px; }
.bai-info h4{ margin:0; font-size:15px; font-weight:600; line-height:1.25; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.bai-price{ font-size:14px; font-weight:500; }
.bai-dots{ display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.bai-dot{ width:16px; height:16px; border-radius:50%; border:1px solid rgba(0,0,0,.35); }
.bai-dot.best{ box-shadow:0 0 0 2px #fff, 0 0 0 3.5px ${INK}; }
.bai-acts{ display:flex; gap:8px; margin-top:2px; flex-wrap:wrap; }
.bai-empty{ margin-top:12px; padding:26px 16px; text-align:center; border:1px dashed #BDBDBD; border-radius:18px; color:${BODY}; font-size:14px; line-height:1.5; }
.bai-fab{ position:fixed; right:18px; bottom:calc(96px + env(safe-area-inset-bottom)); z-index:140; width:58px; height:58px; border-radius:50%; background:${YEL_GLOSS}; border:2px solid ${INK}; color:${INK};
  display:flex; align-items:center; justify-content:center; box-shadow:0 5px 0 #C99700, 0 14px 22px -8px rgba(0,0,0,.5); cursor:pointer; transition:transform .15s; }
.bai-fab:hover{ transform:translateY(-3px); } .bai-fab:active{ transform:translateY(3px); }
@media (prefers-reduced-motion: reduce){ .bai-spin{ animation:none; } .bai *{ transition:none !important; } }
`;

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth <= bp : false);
  useEffect(() => { const f = () => setM(window.innerWidth <= bp); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, [bp]);
  return m;
}

/** optional launcher button: <BeautyAIFab onClick={() => setAiOpen(true)} /> */
export const BeautyAIFab: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <><style>{CSS}</style><button type="button" className="bai bai-fab" aria-label="Open Beauty AI" onClick={onClick}><Sparkles size={26} strokeWidth={1.8} /></button></>
);

/* ═══════════════════════════ COMPONENT ═══════════════════════════ */
type Phase = "idle" | "live" | "analyzing" | "done";

export default function BAi({ open, onClose, products, onView, onAdd, apiUrl = BEAUTY_API_URL, apiKey }: Props) {
  const isMobile = useIsMobile();
  const drag = useDragControls();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [still, setStill] = useState<string | null>(null);
  const [tone, setTone] = useState<Tone | null>(null);
  const [source, setSource] = useState<"api" | "device" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lightNote, setLightNote] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");

  const stopCamera = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  const startCamera = async (face: "user" | "environment" = facing) => {
    stopCamera(); setErr(null); setStill(null);
    if (!navigator.mediaDevices?.getUserMedia) { setErr("This browser can't open the camera. Use HTTPS and a recent browser."); setPhase("idle"); return; }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: face, width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false });
      setPhase("live");
    } catch (e: any) {
      setErr(e?.name === "NotAllowedError" ? "Camera access was blocked. Allow the camera in your browser settings, then try again." : "Couldn't open the camera. Check that no other app is using it.");
      setPhase("idle");
    }
  };

  useEffect(() => {
    if (phase !== "live" || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
  }, [phase, facing]);

  useEffect(() => { const t = setTimeout(() => setDebounced(text), 250); return () => clearTimeout(t); }, [text]);

  useEffect(() => {
    if (!open) { stopCamera(); return; }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); stopCamera(); };
  }, [open, onClose]);

  const flip = () => { const f = facing === "user" ? "environment" : "user"; setFacing(f); startCamera(f); };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const scale = Math.min(1, 960 / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, c.width, c.height);
    setStill(c.toDataURL("image/jpeg", 0.85));
    stopCamera();
    setPhase("analyzing"); setErr(null); setLightNote(null);

    const local = sampleSkin(ctx, c.width, c.height);
    if (local) {
      if (local.light < 70) setLightNote("The photo is quite dark, so the tone may read deeper than it is. Retake near a window for best results.");
      else if (local.light > 225) setLightNote("The photo is very bright, so the tone may read lighter than it is. Retake out of direct light.");
    }
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/jpeg", 0.9));

    let api: Partial<Tone> | null = null;
    try { if (blob) api = fromApi(await callApi(apiUrl, apiKey, blob)); } catch { api = null; }

    if (api) {
      const hex = api.hex ?? local?.hex ?? "#c68642";
      setTone({ hex, depth: api.depth ?? 3, undertone: api.undertone ?? "neutral", label: api.label ?? "", skinType: api.skinType, concerns: api.concerns ?? [] });
      setSource("api"); setPhase("done");
    } else if (local) {
      setTone({ ...describeTone(local.hex), concerns: [] });
      setSource("device"); setPhase("done");
    } else {
      setErr("Couldn't read your skin tone from that photo. Face the light and try again.");
      setPhase("idle");
    }
  };

  const needs = useMemo(() => parseNeeds(debounced), [debounced]);
  const hasInput = !!tone || needs.effects.length > 0 || needs.avoid.length > 0 || needs.extras.length > 0;
  const result = useMemo(() => (hasInput ? recommend(products, tone, needs) : null), [hasInput, products, tone, needs]);

  const retake = () => { setTone(null); setSource(null); setStill(null); startCamera(); };

  const stage = (
    <div className="bai-stage">
      {phase === "idle" && (
        <div className="bai-ph">
          <Camera size={44} strokeWidth={1.2} />
          <p>Face a window, remove filters, and keep your face inside the oval. Uploads from your gallery are turned off, so every scan is live.</p>
          <button type="button" className="bai-btn" onClick={() => startCamera()}>Open camera</button>
        </div>
      )}
      {phase === "live" && (
        <>
          <video ref={videoRef} playsInline muted autoPlay className={facing === "user" ? "mirror" : ""} />
          <div className="bai-oval" />
          <div className="bai-ctrl">
            <button type="button" className="bai-mini" aria-label="Close camera" onClick={() => { stopCamera(); setPhase("idle"); }}><X size={20} /></button>
            <button type="button" className="bai-shutter" aria-label="Scan my skin tone" onClick={capture} />
            <button type="button" className="bai-mini" aria-label="Switch camera" onClick={flip}><SwitchCamera size={20} /></button>
          </div>
        </>
      )}
      {(phase === "analyzing" || phase === "done") && still && <img className="still" src={still} alt="Your scan" style={{ transform: facing === "user" ? "scaleX(-1)" : undefined }} />}
      {phase === "analyzing" && <div className="bai-over"><Loader2 className="bai-spin" size={30} />Reading your skin tone…</div>}
    </div>
  );

  const body = (
    <div className="bai-body">
      <div>
        {stage}
        {err && <div className="bai-err" role="alert"><AlertTriangle size={18} style={{ flex: "none", marginTop: 1 }} /><span>{err}</span></div>}
        {lightNote && phase === "done" && <p className="bai-note">{lightNote}</p>}
        {phase === "done" && tone && (
          <>
            <div className="bai-tone">
              <span className="bai-swatch" style={{ background: tone.hex }} />
              <div>
                <b>{tone.label}</b>
                <small>{tone.hex.toUpperCase()}{tone.skinType ? ` · ${tone.skinType} skin` : ""}</small>
                {tone.concerns.length > 0 && <div className="bai-chips">{tone.concerns.slice(0, 4).map((c) => <span className="bai-chip" key={c}>{c}</span>)}</div>}
              </div>
            </div>
            {source === "device" && <p className="bai-note">BeautyDetector isn't reachable yet, so this is an on-device colour estimate. Skin type and concerns need the API; type them on the right and the picks still adapt.</p>}
            <button type="button" className="bai-btn ghost block" onClick={retake}><RefreshCw size={15} /> Retake</button>
          </>
        )}
      </div>

      <div>
        <label className="bai-label" htmlFor="bai-needs">Skin effects or allergies</label>
        <textarea
          id="bai-needs" className="bai-text" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="e.g. oily skin, want a natural glow. Allergic to fragrance and almond."
        />
        <p className="bai-hint">Tell us what you want and what to avoid. We read each product's title and description.</p>
        {(needs.effects.length > 0 || needs.avoid.length > 0) && (
          <div className="bai-chips">
            {needs.effects.map((e) => <span className="bai-chip fill" key={e}>{cap(e)}</span>)}
            {needs.avoid.map((a) => <span className="bai-chip no" key={a}>Avoiding {a}</span>)}
          </div>
        )}

        <div className="bai-sec"><h3>{hasInput ? "Picked for you" : "Your picks"}</h3></div>

        {!result && <div className="bai-empty">Scan your skin tone or describe what you need, and matching products appear here.</div>}
        {result && result.beautyCount === 0 && <div className="bai-empty">No beauty products in the store yet. Check back soon.</div>}
        {result && result.beautyCount > 0 && result.recs.length === 0 && <div className="bai-empty">Nothing safe matched. Every beauty product mentions an ingredient you want to avoid.</div>}
        {result && result.hidden.length > 0 && (
          <p className="bai-note">Hidden products that mention: {result.hidden.join(", ")}. We only read titles and descriptions, so always check the full ingredient list before buying.</p>
        )}

        {result && result.recs.length > 0 && (
          <div className="bai-list">
            {result.recs.map((r, i) => {
              const cover = coverOf(r.p);
              const vs = variantsOf(r.p).filter((v) => v.hex && isHex(v.hex)).slice(0, 8);
              return (
                <div className={`bai-rec${i === 0 ? " top" : ""}`} key={r.p.productId}>
                  <div className="bai-pic">{cover ? <img src={cover} alt={r.p.name} loading="lazy" /> : <PackageOpen size={28} strokeWidth={1.2} />}</div>
                  <div className="bai-info">
                    {i === 0 && <span className="bai-chip fill" style={{ alignSelf: "flex-start" }}>Best match</span>}
                    <h4>{r.p.name}</h4>
                    <span className="bai-price">{money((r.p as any).price)}</span>
                    {r.reasons.length > 0 && <div className="bai-chips" style={{ marginTop: 0 }}>{r.reasons.slice(0, 3).map((x) => <span className="bai-chip" key={x}>{x}</span>)}</div>}
                    {vs.length > 0 && (
                      <div className="bai-dots" aria-label="Shades">
                        {vs.map((v) => <span key={v.color} title={v.color} className={`bai-dot${r.bestShade === v.color ? " best" : ""}`} style={{ background: v.hex as string }} />)}
                      </div>
                    )}
                    <div className="bai-acts">
                      {onView && <button type="button" className="bai-btn sm ghost" onClick={() => { onClose(); onView(r.p.productId); }}><Eye size={14} /> View</button>}
                      {onAdd && <button type="button" className="bai-btn sm" onClick={() => onAdd(r.p)}><ShoppingBag size={14} /> Add</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={`bai bai-scrim ${isMobile ? "m" : "d"}`} onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <style>{CSS}</style>
          <motion.div
            role="dialog" aria-modal="true" aria-label="Beauty AI"
            className={`bai-panel ${isMobile ? "m" : "d"}`}
            onClick={(e) => e.stopPropagation()}
            initial={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.96, y: 14 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.96, y: 14 }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            drag={isMobile ? "y" : false} dragControls={drag} dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => { if (info.offset.y > 110 || info.velocity.y > 600) onClose(); }}
          >
            {isMobile && <div className="bai-handle" onPointerDown={(e) => drag.start(e)}><i /></div>}
            <div className="bai-head">
              <Sparkles size={26} strokeWidth={1.6} />
              <div>
                <h2>Beauty AI</h2>
                <p>Find shades and products that suit your skin</p>
              </div>
              <button type="button" className="bai-x" aria-label="Close" onClick={onClose}><X size={18} strokeWidth={1.8} /></button>
            </div>
            {body}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}